"use client";

import { Button as ButtonComponent } from "@/components/ui/button";
import { ButtonStoryblok } from "@/component-types-sb";
import { createSlug } from "@/lib/utils";
import { cn, scrollTo } from "@/lib/utils";
import ButtonReserve from "@/components/ButtonReserve";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useDatasourcesStore } from "@/components/provider/DatasourcesStoreProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar-lazy";
import { Label } from "@/components/ui/label";
import FieldError from "@/components/FieldError";
import FormGuard from "@/components/FormGuard";
import React from "react";
import LucideIcon from "@/components/LucideIcon";
import { EventBookingStoryblok } from "@/component-types-sb";
import {
  BOOKING_FAILED_MESSAGE,
  BOOKING_INVALID_MESSAGE,
  BookingField,
  BookingFieldErrors,
  hasBookingErrors,
  invalidFieldProps,
  validateBooking,
} from "@/lib/booking";
import { linkResolver } from "@/lib/storyblok_utils";
import { storyblokEditable, StoryblokServerComponent } from "@storyblok/react/rsc";
import { ModalProvider } from "@/components/provider/ModalProvider";


// "tel:+41 55 552 80 00" → "Anrufen: +41 55 552 80 00", "https://www.facebook.com/…" → "facebook.com"
function labelFromHref(href: string | undefined) {
  if (!href) return undefined;
  if (href.startsWith("tel:")) return `Anrufen: ${href.slice(4)}`;
  if (href.startsWith("mailto:")) return `E-Mail an ${href.slice(7)}`;
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

const Button: React.FC<{ blok: ButtonStoryblok; className?: string }> = ({
  blok,
  className,
}) => {
  const datasources = useDatasourcesStore((state) => state.datasources);
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<BookingFieldErrors>({});
  const href = blok.href || linkResolver(blok.href_dynamic?.cached_url) || blok.href_dynamic?.url;
  // A button that shows only its icon is named by its title, or else by where
  // it leads. An empty aria-label hides the link's name entirely.
  const iconLabel = blok.icon ? blok.title || labelFromHref(href) : undefined;

  const variant =
    blok.variant === "primary"
      ? "default"
      : (blok.variant as
          | "outline"
          | "default"
          | "ghost"
          | "secondary"
          | "destructive"
          | "link"
          | null
          | undefined);
  const sharedClassNames = cn(
    "rounded-full uppercase cursor-pointer",
    className
  );

  // Function to format date in German
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const clearFieldError = (e: React.FormEvent<HTMLFormElement>) => {
    const field = (e.target as HTMLInputElement).name as BookingField;
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(null);
    setError(null);
    setFieldErrors({});

    const formData = new FormData(e.currentTarget);
    const reservationData: EventBookingStoryblok = {
      component: "event_booking",
      _uid: "",
    };
    formData.forEach((value, key) => {
      reservationData[key] = value;
    });
    reservationData.date = date?.toISOString();

    const validationErrors = validateBooking(reservationData);
    if (hasBookingErrors(validationErrors)) {
      setFieldErrors(validationErrors);
      setError(BOOKING_INVALID_MESSAGE);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/api/storyblok/addEventBooking", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reservationData),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFieldErrors(result.fieldErrors || {});
        throw new Error(result.message || BOOKING_FAILED_MESSAGE);
      }

      setSuccess(
        result.message || "Deine Anfrage wurde erfolgreich übermittelt!"
      );
      (e.target as HTMLFormElement).reset();
      setDate(new Date());
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message || BOOKING_FAILED_MESSAGE
          : "Unbekannter Fehler"
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocalScroll = () => {
    if (!blok.href)
      return;

    const slug = createSlug(blok.href);
    const element = document.getElementById(slug);

    if (element) {
      const compactHeaderHeight = 158;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - compactHeaderHeight;

      scrollTo(offsetPosition, () => {
        window.scrollTo({
          top: offsetPosition - 1,
          behavior: "smooth",
        });
      });
    }
  };

  switch (blok.type) {
    case "localScroll":
      return (
        <ButtonComponent
          {...storyblokEditable(blok)}
          variant={variant}
          onClick={handleLocalScroll}
          className={sharedClassNames}
          aria-label={iconLabel}
        >
          {!blok.icon && blok.title}
          {blok.icon && <LucideIcon name={blok.icon} className="h-6 w-6" />}
        </ButtonComponent>
      );

    case "reserve":
      return (
        <ButtonReserve
          {...storyblokEditable(blok)}
          variant={variant}
          className={sharedClassNames}
          aria-label={iconLabel}
        >
          {!blok.icon && blok.title}
          {blok.icon && <LucideIcon name={blok.icon} className="h-6 w-6" />}
        </ButtonReserve>
      );

    case "event":
      return (
        <Dialog>
          <DialogTrigger asChild>
            <ButtonComponent
              {...storyblokEditable(blok)}
              variant={variant}
              className={sharedClassNames}
              aria-label={iconLabel}
            >
              {!blok.icon && blok.title}
              {blok.icon && <LucideIcon name={blok.icon} className="h-6 w-6" />}
            </ButtonComponent>
          </DialogTrigger>
          <DialogContent className="overflow-y-auto max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{datasources["event-booking"]?.title}</DialogTitle>
              <DialogDescription>
                {datasources["event-booking"]?.description}
              </DialogDescription>
            </DialogHeader>
            {success ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-green-800">{success}</p>
              </div>
            ) : (
              <form className="grid gap-4" onSubmit={handleSubmit} onChange={clearFieldError} noValidate>
                <div className="grid gap-2">
                  <Label htmlFor="event-name">Name *</Label>
                  <Input
                    id="event-name"
                    name="name"
                    type="text"
                    placeholder="Name"
                    required
                    {...invalidFieldProps(fieldErrors, "name", "event-name")}
                  />
                  <FieldError id="event-name-error" message={fieldErrors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="event-phone">Telefon *</Label>
                  <Input
                    id="event-phone"
                    name="phone"
                    type="tel"
                    placeholder="Telefon"
                    required
                    {...invalidFieldProps(fieldErrors, "phone", "event-phone")}
                  />
                  <FieldError id="event-phone-error" message={fieldErrors.phone} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="event-email">Email *</Label>
                  <Input
                    id="event-email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    required
                    {...invalidFieldProps(fieldErrors, "email", "event-email")}
                  />
                  <FieldError id="event-email-error" message={fieldErrors.email} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="event-message">Nachricht</Label>
                  <Textarea
                    id="event-message"
                    name="message"
                    placeholder="Nachricht"
                    rows={3}
                    className="resize-none"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="event-people">Anzahl Personen *</Label>
                  <Input
                    id="event-people"
                    name="people"
                    type="number"
                    placeholder="Anzahl Personen"
                    required
                    min={1}
                    {...invalidFieldProps(fieldErrors, "people", "event-people")}
                  />
                  <FieldError id="event-people-error" message={fieldErrors.people} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="event-time">Uhrzeit *</Label>
                  <Input
                    id="event-time"
                    name="time"
                    type="time"
                    placeholder="Uhrzeit"
                    defaultValue="18:00"
                    required
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                    {...invalidFieldProps(fieldErrors, "time", "event-time")}
                  />
                  <FieldError id="event-time-error" message={fieldErrors.time} />
                </div>

                <div className="grid gap-2">
                  <Label>Datum *</Label>

                  <Input
                    className="w-full justify-start text-left font-normal"
                    {...invalidFieldProps(fieldErrors, "date", "event-date")}
                    value={date ? formatDate(date) : "Datum wählen"}
                    readOnly
                  />

                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(selectedDate) => {
                      setDate(selectedDate);
                      setFieldErrors((prev) => ({ ...prev, date: undefined }));
                    }}
                    disabled={(date) =>
                      date < new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    formatters={{
                      formatWeekdayName: (date) =>
                        date.toLocaleDateString("de-DE", { weekday: "short" }),
                      formatMonthCaption: (date) =>
                        date.toLocaleDateString("de-DE", {
                          month: "long",
                          year: "numeric",
                        }),
                    }}
                  />
                  <FieldError id="event-date-error" message={fieldErrors.date} />
                </div>
                <Input
                  id="event-context"
                  name="context"
                  type="hidden"
                  value={blok.context}
                />
                <FormGuard />

                <ButtonComponent
                  type="submit"
                  variant="default"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Wird gesendet..." : "Anfragen"}
                </ButtonComponent>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-red-800">{error}</p>
                  </div>
                )}
              </form>
            )}
          </DialogContent>
        </Dialog>
      );

    case "modal":
      return (
        <Dialog>
          <DialogTrigger asChild>
            <ButtonComponent
              {...storyblokEditable(blok)}
              variant={variant}
              className={sharedClassNames}
              aria-label={iconLabel}
            >
              {!blok.icon && blok.title}
              {blok.icon && <LucideIcon name={blok.icon} className="h-6 w-6" />}
            </ButtonComponent>
          </DialogTrigger>
          <DialogContent className="overflow-y-auto max-h-[90vh]">
            <DialogHeader className="sr-only">
              <DialogTitle>{blok.title}</DialogTitle>
            </DialogHeader>
            <ModalProvider value={true}>
              {blok.modal_body?.map((nestedBlok) => (
                <StoryblokServerComponent
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                />
              ))}
            </ModalProvider>
          </DialogContent>
        </Dialog>
      );

    default:
      return (
        <ButtonComponent
          {...storyblokEditable(blok)}
          variant={variant}
          asChild
          className={sharedClassNames}
          aria-label={iconLabel}
        >
          <Link href={href || "#"} passHref>
            {!blok.icon && blok.title}
            {blok.icon && <LucideIcon name={blok.icon} className="h-6 w-6" />}
          </Link>
        </ButtonComponent>
      );
  }
};

export default Button;
